import { useCallback } from 'react';

import { usePico } from '../pico';
import { usePageLabel } from './use-page-label';

/**
 * The surface that rendered the error page. `php_legacy` is fired server-side
 * from the PHP exception controller and is included here only for type symmetry.
 */
export type ErrorPageType = 'vimeo_next' | 'showcase' | 'ai_widget' | 'php_legacy';

/**
 * Categorises the error class that triggered the page. Call sites resolve this
 * from their own `instanceof` check (which they already maintain for rendering),
 * so picox stays agnostic to `@vimeo/core` error classes.
 */
export type ErrorPageErrorType =
  | 'rate_limit'
  | 'resource_not_found'
  | 'forbidden'
  | 'unauthorized'
  | 'not_available'
  | 'clips_fetch'
  | 'technical_difficulties';

export type ErrorPageDisplayedProps = {
  errorPageType: ErrorPageType;
  errorType: ErrorPageErrorType;
  /** HTTP status code associated with the error, when known. `null` for the
   * default `technical_difficulties` branch and other domain errors that have
   * no reliable code (e.g. `clips_fetch`). */
  errorPageErrorCode: number | null;
};

export const useErrorPageTracking = () => {
  const pico = usePico();
  const errorPageLocation = usePageLabel();

  const trackErrorPageDisplayed = useCallback(
    (props: ErrorPageDisplayedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('error_page_displayed', {
        error_page_type: props.errorPageType,
        error_page_error_code: props.errorPageErrorCode,
        error_page_location: errorPageLocation,
        error_type: props.errorType,
      });
    },
    [pico, errorPageLocation],
  );

  return { trackErrorPageDisplayed };
};
